import { MEDICAL_PROVIDER_INPUT_MODE, UNASSIGNED_PROVIDER_NAME } from '@demandproai/shared';

export const PROVIDER_TIMING_SECTIONS = ['postAccident', 'preAccident'];

/**
 * Reports whether a medicalRecords entry is an AI-resolved provider. A resolved
 * provider has a non-empty name that is not the __UNASSIGNED__ placeholder.
 * @param {{ providerName?: string }} [provider] - a medicalRecords entry
 * @returns {boolean}
 */
export const getIsResolvedProvider = (provider) => {
  const name = (provider?.providerName ?? '').trim();
  return name !== '' && name !== UNASSIGNED_PROVIDER_NAME;
};

/**
 * Selects the providers a timing section should render as editable cards, in both
 * AI and MANUAL modes. Keeps every resolved provider (named, non-__UNASSIGNED__)
 * while excluding the __UNASSIGNED__ bucket placeholder, which lives in the upload
 * drop-zone rather than the list. Preserves each record's true index within the
 * unfiltered medicalRecords array so edit/delete/upload target the right record.
 * @param {Array<{ providerName?: string }>} [medicalRecords] - a timing section's records
 * @returns {Array<{ provider: Object, providerIndex: number }>}
 */
export const selectRenderableProviders = (medicalRecords) => {
  const records = Array.isArray(medicalRecords) ? medicalRecords : [];
  return records
    .map((provider, providerIndex) => ({ provider, providerIndex }))
    .filter(({ provider }) => getIsResolvedProvider(provider));
};

/**
 * Splits a timing section's medicalRecords into the AI-resolved providers and
 * the single unresolved bucket entry. In AI mode every named entry (anything
 * other than the __UNASSIGNED__ placeholder) is a provider the dedup pipeline
 * resolved; the placeholder entry, if present, still holds records awaiting
 * resolution.
 * @param {Array<{ providerName?: string, source?: Array }>} [medicalRecords] - a timing section's records
 * @returns {{ resolved: Array, unassigned: Object|null, pendingCount: number }}
 */
export const splitResolvedProviders = (medicalRecords) => {
  const records = Array.isArray(medicalRecords) ? medicalRecords : [];
  const unassigned = records.find(({ providerName } = {}) => providerName === UNASSIGNED_PROVIDER_NAME) || null;
  const resolved = records.filter(getIsResolvedProvider);
  const pendingCount = unassigned?.source?.length ?? 0;

  return { resolved, unassigned, pendingCount };
};

// mirrors useMedicalProvidersInputMode's fallback: an absent or empty mode reads as AI
const getIsClientInAiMode = (client) =>
  (client?.medicalProvidersInputMode || MEDICAL_PROVIDER_INPUT_MODE.AI) === MEDICAL_PROVIDER_INPUT_MODE.AI;

/**
 * Reports whether a timing section is still waiting on AI provider resolution, i.e. its
 * pending providers bucket holds files. Only AI mode waits; MANUAL mode edits freely.
 * @param {Object} params
 * @param {Array<{ providerName?: string, source?: Array }>} [params.medicalRecords] - a timing section's records
 * @param {boolean} params.isAiMode
 * @returns {boolean}
 */
export const getIsSectionPendingAiResolution = ({ medicalRecords, isAiMode }) =>
  Boolean(isAiMode) && splitResolvedProviders(medicalRecords).pendingCount > 0;

/**
 * Names the timing sections on a client that are still waiting on AI provider resolution.
 * Single source of truth for both the locked provider list and the resolution poll's gate.
 * @param {Object} [client] - a clients[] entry
 * @returns {string[]} pending section keys, in PROVIDER_TIMING_SECTIONS order
 */
export const getPendingAiResolutionSections = (client) => {
  const isAiMode = getIsClientInAiMode(client);
  return PROVIDER_TIMING_SECTIONS.filter((section) => getIsSectionPendingAiResolution({
    medicalRecords: client?.medicalProviders?.[section]?.medicalRecords,
    isAiMode
  }));
};

/**
 * Reports whether any timing section on a client is still waiting on AI provider resolution.
 * @param {Object} [client] - a clients[] entry
 * @returns {boolean}
 */
export const getIsClientPendingAiResolution = (client) => getPendingAiResolutionSections(client).length > 0;

/**
 * Locates where a bucket upload should write within a timing section's records.
 * Points at the existing __UNASSIGNED__ entry when present (so resolved
 * providers are left untouched), otherwise at a fresh slot appended at the end.
 * @param {Array<{ providerName?: string, source?: Array }>} records - a timing section's records
 * @returns {{ exists: boolean, index: number, source: Array }}
 */
export const resolveBucketTarget = (records) => {
  const index = records.findIndex(({ providerName } = {}) => providerName === UNASSIGNED_PROVIDER_NAME);
  const exists = index !== -1;

  return {
    exists,
    index: exists ? index : records.length,
    source: exists ? (records[index]?.source || []) : []
  };
};

/**
 * Builds a stable React key for a resolved provider. Prefers an explicit id,
 * otherwise derives one from the provider name and its source paths so the key
 * stays stable across re-renders without relying on array index.
 * @param {Object} params
 * @param {{ id?: string, providerId?: string, providerName?: string, name?: string, source?: Array }} params.provider
 * @returns {string}
 */
export const buildProviderKey = ({ provider }) => {
  const explicitId = provider?.id || provider?.providerId;
  if (explicitId) return String(explicitId);

  const providerName = provider?.providerName || provider?.name || 'unknown-provider';
  const sourceKey = (provider?.source || [])
    .map((source) => source?.s3UrlPath || source?.name || '')
    .filter(Boolean)
    .join('|');

  return `${providerName}::${sourceKey}`;
};
